import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage, API_URL, getAuthHeaders } from '../utils/api';
import { filenameFromContentDisposition } from './useEssayApi';
import { showToast } from '../utils/toast';

export type ScoreAuthorRole = 'lead' | 'pm' | 'qa' | 'devops' | 'other';

/** Диапазон оценки. Целые: дробные проценты — ложная точность. */
export const MIN_SCORE = 0;
export const MAX_SCORE = 100;

export interface MyScore {
  /** `null` — отказ «не могу оценить»: ответ без цифры. */
  value: number | null;
  /** У отказа здесь причина. */
  comment: string | null;
  isAbstained: boolean;
}

/** Строка на экране оценивающего: человек, его отчёт и МОЯ оценка. */
export interface ScoreTarget {
  userId: string;
  name: string;
  position?: string;
  /** `null` — отчёта нет или он не сдан: оценить нечего. */
  reportId: string | null;
  hasReport: boolean;
  myScore: MyScore | null;
}

/**
 * Окно оценивания квартала. Дни — календарные `YYYY-MM-DD`, а не моменты
 * времени: иначе браузер переведёт их в свой пояс и в Новосибирске покажет
 * «до 15-го».
 */
export interface ScoringWindow {
  state: 'pending' | 'open' | 'closed';
  firstDay: string;
  lastDay: string;
}

export interface ScoreTargets {
  window: ScoringWindow;
  targets: ScoreTarget[];
}

export interface AssignmentCell {
  subject: string;
  author: string;
  authorRole: ScoreAuthorRole;
}

export interface AssignmentMatrix {
  people: { id: string; name: string; position?: string }[];
  assignments: AssignmentCell[];
}

export interface ScoreRow {
  authorId: string;
  authorName: string;
  authorRole: ScoreAuthorRole;
  value: number;
  comment: string | null;
}

export interface ResultRow {
  userId: string;
  name: string;
  position?: string;
  /** Уволенные остаются в итогах, если их успели оценить. */
  isActive: boolean;
  /** Команды человека. Первая — та, под которой он стоит в списке. */
  teams: string[];
  /** Отчёт за квартал: по нему открывают текст. `null` — отчёта нет. */
  reportId: string | null;
  /** Средняя по ролям. `null` — не оценил никто: это не ноль, ноль — «всё плохо». */
  average: number | null;
  /** Оценка рукой первого лица. `null` — не правили, итог равен средней. */
  finalScore: number | null;
  finalScoreBy: string | null;
  finalScoreAt: string | null;
  /** Итог квартала: поправленное, иначе среднее. */
  total: number | null;
  /** Итог прошлого квартала — чтобы видеть рост или падение. */
  previousTotal: number | null;
  count: number;
  byRole: { role: ScoreAuthorRole; average: number; count: number }[];
  scores: ScoreRow[];
  /** «Не могу оценить» с причиной. В среднее не идут. */
  abstentions: {
    authorId: string;
    authorName: string;
    authorRole: ScoreAuthorRole;
    reason: string;
  }[];
  /** Назначены, но ещё не ответили. */
  pending: { authorId: string; authorName: string }[];
  /** Смотрящий сам не ответил, а окно открыто — чужие оценки скрыты. */
  isHidden: boolean;
}

export interface ScoreResults {
  /** `open` — итоги предварительные. */
  state: 'open' | 'closed';
  lastDay?: string;
  rows: ResultRow[];
}

/** Целое в диапазоне — то же правило, что и на сервере. */
export const isValidScore = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= MIN_SCORE && value <= MAX_SCORE;

/** Назначали ли меня оценивать хоть раз — по этому меню решает, показывать ли пункт «Оценка». */
export const useScoringAccess = (enabled = true) =>
  useQuery({
    queryKey: ['quarterly-scores', 'access'],
    queryFn: async (): Promise<{ hasAssignments: boolean }> => api('/quarterly-scores/access'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useScoreTargets = (year: number, quarter: number, enabled = true) =>
  useQuery({
    queryKey: ['quarterly-scores', 'targets', year, quarter],
    queryFn: async (): Promise<ScoreTargets> =>
      api(`/quarterly-scores/targets?year=${year}&quarter=${quarter}`),
    enabled,
  });

/** Общий обработчик отказа: сервер отклоняет по делу — закрытое окно, снятое
 *  назначение, несданный отчёт. Без тоста поле выглядело бы сломанным. */
const useMutationToast = () => {
  const { t } = useTranslation();
  return (error: unknown) =>
    showToast({
      title: t('common.error'),
      message: getErrorMessage(error, t('quarterlyScoring.saveFailed')),
      color: 'red',
    });
};

export const useSetScore = () => {
  const qc = useQueryClient();
  const onError = useMutationToast();
  return useMutation({
    mutationFn: async (
      p:
        | { reportId: string; value: number; comment?: string }
        | { reportId: string; isAbstained: true; comment: string },
    ) =>
      api(`/quarterly-scores/${p.reportId}`, {
        method: 'PUT',
        body:
          'isAbstained' in p
            ? { isAbstained: true, comment: p.comment }
            : { value: p.value, comment: p.comment },
      }),
    onSuccess: (_, p) => {
      trackEvent(AnalyticsEvents.QUARTERLY_SCORE_SAVED, { abstained: 'isAbstained' in p });
      return qc.invalidateQueries({ queryKey: ['quarterly-scores'] });
    },
    onError,
  });
};

export const useRemoveScore = () => {
  const qc = useQueryClient();
  const onError = useMutationToast();
  return useMutation({
    mutationFn: async (p: { reportId: string; authorId: string }) =>
      api(`/quarterly-scores/${p.reportId}/${p.authorId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quarterly-scores'] }),
    onError,
  });
};

export const useAssignmentMatrix = (year: number, quarter: number, enabled = true) =>
  useQuery({
    queryKey: ['quarterly-scores', 'assignments', year, quarter],
    queryFn: async (): Promise<AssignmentMatrix> =>
      api(`/quarterly-scores/assignments?year=${year}&quarter=${quarter}`),
    enabled,
  });

export const useSetAssignment = () => {
  const qc = useQueryClient();
  const onError = useMutationToast();
  return useMutation({
    mutationFn: async (p: {
      year: number;
      quarter: number;
      subject: string;
      author: string;
      isAssigned: boolean;
    }) => api('/quarterly-scores/assignments', { method: 'PUT', body: p }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quarterly-scores', 'assignments'] }),
    onError,
  });
};

export const usePrefillAssignments = () => {
  const qc = useQueryClient();
  const onError = useMutationToast();
  return useMutation({
    mutationFn: async (p: { year: number; quarter: number }): Promise<{ added: number }> =>
      api(`/quarterly-scores/assignments/prefill?year=${p.year}&quarter=${p.quarter}`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quarterly-scores', 'assignments'] }),
    onError,
  });
};

/**
 * Итоги квартала. С `isPreview` сервер отдаёт их и при открытом окне как
 * предварительные; без него — только после закрытия, голым массивом.
 */
export const useScoreResults = (year: number, quarter: number, enabled = true, isPreview = false) =>
  useQuery({
    queryKey: ['quarterly-scores', 'results', year, quarter, isPreview],
    queryFn: async (): Promise<ScoreResults> => {
      const url = `/quarterly-scores/results?year=${year}&quarter=${quarter}`;
      if (isPreview) return api(`${url}&preview=1`);
      return { state: 'closed', rows: await api(url) };
    },
    enabled,
    // 409 «окно ещё открыто» — не сбой связи, повторять его бессмысленно.
    retry: false,
  });

/**
 * Итоговая оценка от первого лица. `value: null` снимает правку и возвращает
 * среднюю: посчитанное никуда не девалось, его просто перестают перекрывать.
 */
export const useSetFinalScore = () => {
  const qc = useQueryClient();
  const onError = useMutationToast();
  return useMutation({
    mutationFn: async (p: {
      year: number;
      quarter: number;
      userId: string;
      value: number | null;
    }) => api('/quarterly-scores/final', { method: 'PUT', body: p }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quarterly-scores'] }),
    onError,
  });
};

export async function downloadResultsXlsx(year: number, quarter: number) {
  const response = await fetch(
    `${API_URL}/quarterly-scores/results/export?year=${year}&quarter=${quarter}`,
    { headers: getAuthHeaders() },
  );
  if (!response.ok) throw new Error('Quarterly results export failed');
  const blob = await response.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download =
    filenameFromContentDisposition(response.headers.get('content-disposition')) ||
    'quarterly-results.xlsx';
  a.click();
  URL.revokeObjectURL(a.href);
}
